import { apiClient } from '@/utils/api-client';
import {
  DocumentTemplateList,
  DocumentTemplateCreateHTML,
  EmailTemplateList,
  DocumentTemplateDetail,
  EmailTemplateDetail,
  TemplateUploadResponse,
  DocumentTemplateUpdate,
  EmailTemplateUpdate,
  EmailTemplateCreate,
} from '@/types/template';

// Document Templates

export async function getDocumentTemplates(filters?: {
  template_type?: string;
  source?: string;
  search?: string;
}): Promise<DocumentTemplateList[]> {
  const params = new URLSearchParams();
  if (filters?.template_type)
    params.set('template_type', filters.template_type);
  if (filters?.source) params.set('source', filters.source);
  if (filters?.search) params.set('search', filters.search);
  const queryString = params.toString();
  const url = queryString
    ? `/admin/templates/document?${queryString}`
    : '/admin/templates/document';
  const response = await apiClient.get<DocumentTemplateList[]>(url);
  return response;
}

export async function createHtmlDocumentTemplate(
  data: DocumentTemplateCreateHTML
): Promise<DocumentTemplateDetail> {
  const response = await apiClient.post<DocumentTemplateDetail>(
    '/admin/templates/document/html',
    data
  );
  return response;
}

export async function setDefaultDocumentTemplate(
  templateId: string
): Promise<void> {
  await apiClient.post(`/admin/templates/document/${templateId}/set-default`);
}

export async function deactivateDocumentTemplate(
  templateId: string
): Promise<void> {
  await apiClient.post(`/admin/templates/document/${templateId}/deactivate`);
}

export async function getDocumentTemplatePreviewHtml(
  templateId: string
): Promise<{ html: string }> {
  const response = await apiClient.post<{ html: string }>(
    `/admin/templates/document/${templateId}/preview-html`
  );
  return response;
}

export async function getDocumentTemplate(
  templateId: string
): Promise<DocumentTemplateDetail> {
  const response = await apiClient.get<DocumentTemplateDetail>(
    `/admin/templates/document/${templateId}`
  );
  return response;
}

export async function uploadDocumentTemplate(data: {
  file: File;
  name: string;
  description?: string;
  template_type: string;
  sample_data?: Record<string, unknown>;
}): Promise<TemplateUploadResponse> {
  const formData = new FormData();
  formData.append('file', data.file);
  formData.append('name', data.name);
  if (data.description) {
    formData.append('description', data.description);
  }
  formData.append('template_type', data.template_type);
  if (data.sample_data) {
    formData.append('sample_data', JSON.stringify(data.sample_data));
  }

  const response = await apiClient.post<TemplateUploadResponse>(
    '/admin/templates/document',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response;
}

export async function updateDocumentTemplate(
  templateId: string,
  data: DocumentTemplateUpdate
): Promise<DocumentTemplateDetail> {
  const response = await apiClient.put<DocumentTemplateDetail>(
    `/admin/templates/document/${templateId}`,
    data
  );
  return response;
}

export async function deleteDocumentTemplate(
  templateId: string
): Promise<void> {
  await apiClient.delete(`/admin/templates/document/${templateId}`);
}

// Email Templates

export async function getEmailTemplates(): Promise<EmailTemplateList[]> {
  const response = await apiClient.get<EmailTemplateList[]>(
    '/admin/templates/email'
  );
  return response;
}

export async function getEmailTemplate(
  templateId: string
): Promise<EmailTemplateDetail> {
  const response = await apiClient.get<EmailTemplateDetail>(
    `/admin/templates/email/${templateId}`
  );
  return response;
}

export async function createEmailTemplate(
  data: EmailTemplateCreate
): Promise<EmailTemplateDetail> {
  const response = await apiClient.post<EmailTemplateDetail>(
    '/admin/templates/email',
    data
  );
  return response;
}

export async function updateEmailTemplate(
  templateId: string,
  data: EmailTemplateUpdate
): Promise<EmailTemplateDetail> {
  const response = await apiClient.put<EmailTemplateDetail>(
    `/admin/templates/email/${templateId}`,
    data
  );
  return response;
}

export async function deactivateEmailTemplate(
  templateId: string
): Promise<void> {
  await apiClient.post(`/admin/templates/email/${templateId}/deactivate`);
}

export async function setDefaultEmailTemplate(
  templateId: string
): Promise<void> {
  await apiClient.post(`/admin/templates/email/${templateId}/set-default`);
}

export async function deleteEmailTemplate(templateId: string): Promise<void> {
  await apiClient.delete(`/admin/templates/email/${templateId}`);
}

export async function previewEmailTemplate(
  templateId: string,
  previewData?: Record<string, unknown>
): Promise<{
  subject: string;
  html: string;
  text: string;
}> {
  const response = await apiClient.post<{
    subject: string;
    html: string;
    text: string;
  }>(`/admin/templates/email/${templateId}/preview`, {
    preview_data: previewData || {},
  });
  return response;
}
